import React, { useState, useContext } from "react";
import { formatDate } from "../../../constants/helper";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl, useStateContext } from "../../../contexts/ContextProvider";
import { Loader } from "../../../components";
import {
  Box,
  Typography,
  TextField,
  ButtonGroup,
  Button,
  Divider,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import { Col, Row, Form } from "react-bootstrap";

const lapAngsuranColumns = [
  "kd_ar",
  "nm_ar",
  "alamat",
  "telepon",
  "no_plat",
  "tenor",
  "ke",
  "telat",
  "angsuran",
  "sisamodal",
  "jt_tempo",
  "circle",
  "tgl_bayar",
  "kd_sur",
  "surveyor",
  "surasli",
  "nm_asli",
  "kd_mkt",
  "marketing",
  "tglsp",
  "eksekutor",
  "kode_kec",
  "kecamatan",
  "tgl_ttp",
  "tglst",
  "status",
  "kasus",
];

const cleanCell = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number" && Number.isNaN(value)) return "";
  return value;
};

const normalizeLapAngsuranRows = (rows = []) =>
  rows.map((row) =>
    lapAngsuranColumns.reduce((acc, column) => {
      acc[column] = cleanCell(row[column]);
      return acc;
    }, {})
  );

const readLapAngsuranStream = async ({
  endpoint,
  payload,
  onRows,
  onProgress,
}) => {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, stream: true }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    let message = `Gagal memuat laporan (${response.status})`;

    try {
      message = JSON.parse(responseText).message || message;
    } catch {
      if (responseText) message = responseText;
    }

    throw new Error(message);
  }

  if (!response.body) {
    throw new Error("Browser tidak mendukung streaming laporan");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let totalRows = 0;
  let expectedTotal = null;

  const handleEvent = (event) => {
    if (Array.isArray(event)) {
      totalRows += event.length;
      onRows?.(event, totalRows, expectedTotal);
      return;
    }

    if (event.type === "error") {
      throw new Error(event.message || "Gagal memuat laporan angsuran");
    }

    if (event.type === "meta") {
      if (Number.isFinite(event.total)) expectedTotal = event.total;
      onProgress?.(event, totalRows, expectedTotal);
      return;
    }

    if (event.type === "progress") {
      onProgress?.(event, totalRows, expectedTotal);
      return;
    }

    if (event.type === "rows") {
      totalRows += event.rows?.length || 0;
      onRows?.(event.rows || [], totalRows, expectedTotal);
      return;
    }

    if (event.type === "done") {
      if (Number.isFinite(event.total)) expectedTotal = event.total;
      onProgress?.(event, totalRows, expectedTotal);
    }
  };

  const consume = (chunk) => {
    buffer += decoder.decode(chunk, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.trim()) handleEvent(JSON.parse(line));
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    consume(value);
  }

  buffer += decoder.decode();
  if (buffer.trim()) handleEvent(JSON.parse(buffer));

  return { totalRows, expectedTotal };
};

const downloadExcel = (rows, perTgl, historis = false) => {
  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: lapAngsuranColumns,
  });
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "LapAngsuran");
  XLSX.writeFile(
    workbook,
    `${historis ? "ANGSURAN_HISTORIS" : "ANGSURAN"}_${formatDate(
      perTgl
    ).replaceAll("/", "-")}.xlsx`
  );
};

const LapAngsuran = ({ historis = false }) => {
  const { user, setting } = useContext(AuthContext);
  const reportEndpoint = historis
    ? `${tempUrl}/lapAngsuranHistoris`
    : `${tempUrl}/lapAngsuran`;
  let date = new Date();
  let [perTgl, setPerTgl] = useState(date);
  const [lapAngsuranData, setLapAngsuranData] = useState([]);

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [persenLoading, setPersenLoading] = useState("0");
  const { screenSize } = useStateContext();
  const textRight = {
    textAlign: screenSize >= 650 && "right",
  };

  const columns = [
    { title: "kd_ar", field: "kd_ar" },
    { title: "nm_ar", field: "nm_ar" },
    { title: "alamat", field: "alamat" },
    { title: "telat", field: "telat" },
  ];

  const downloadPdf = async () => {
    setLoading(true);

    try {
      const streamRows = [];
      await readLapAngsuranStream({
        endpoint: reportEndpoint,
        payload: {
          perTgl,
          tipeUser: user.tipeUser,
          id: user._id,
          token: user.token,
          kodeCabang: user.cabang._id,
        },
        onRows: (rows, totalRows, expectedTotal) => {
          streamRows.push(...rows);
          const percent = expectedTotal
            ? Math.min(95, 10 + Math.round((totalRows / expectedTotal) * 85))
            : 50;
          setPersenLoading(percent.toString());
        },
        onProgress: (event, totalRows, expectedTotal) => {
          if (event.type === "done") {
            setPersenLoading("100");
            return;
          }
          const percent = expectedTotal
            ? Math.min(90, 10 + Math.round((totalRows / expectedTotal) * 80))
            : event.type === "meta"
            ? 10
            : 20;
          setPersenLoading(percent.toString());
        },
      });

      const rows = normalizeLapAngsuranRows(streamRows);
      const doc = new jsPDF();
      doc.setFontSize(12);
      doc.text(`${setting.namaPerusahaan} - ${setting.kotaPerusahaan}`, 15, 10);
      doc.text(`${setting.lokasiPerusahaan}`, 15, 15);
      doc.setFontSize(16);
      doc.text(`Laporan Angsuran${historis ? " Historis" : ""}`, 80, 30);
      doc.setFontSize(10);
      doc.text(`Per Tanggal : ${formatDate(perTgl)}`, 15, 35);
      doc.text(
        `Cabang : ${user.cabang._id === "" ? "SEMUA CABANG" : user.cabang._id}`,
        15,
        40
      );
      doc.setFontSize(12);
      function alignCol(data) {
        var col = data.column.index;
        if (col >= 10) data.cell.styles.halign = "right";
      }
      doc.autoTable({
        startY: doc.pageCount > 1 ? doc.autoTableEndPosY() + 20 : 50,
        columns: columns.map((col) => ({ ...col, dataKey: col.field })),
        body: rows,
        headStyles: {
          fillColor: [117, 117, 117],
          color: [0, 0, 0],
        },
        didParseCell: function (cell, data) {
          alignCol(cell, data);
        },
      });
      window.open(URL.createObjectURL(doc.output("blob")));
    } catch (error) {
      window.alert(error.message || "Gagal memuat laporan angsuran");
    } finally {
      setLoading(false);
      setPersenLoading("0");
    }
  };

  const getLapAngsuran = async () => {
    if (!perTgl) {
      setError(true);
      return;
    }

    setLoading(true);
    setLapAngsuranData([]);
    setPersenLoading("5");

    try {
      await readLapAngsuranStream({
        endpoint: reportEndpoint,
        payload: {
          perTgl,
          tipeUser: user.tipeUser,
          id: user._id,
          token: user.token,
          kodeCabang: user.cabang._id,
        },
        onRows: (rows, totalRows, expectedTotal) => {
          setLapAngsuranData((previousRows) => [
            ...previousRows,
            ...normalizeLapAngsuranRows(rows),
          ]);
          const percent = expectedTotal
            ? Math.min(95, 10 + Math.round((totalRows / expectedTotal) * 85))
            : 50;
          setPersenLoading(percent.toString());
        },
        onProgress: (event, totalRows, expectedTotal) => {
          if (event.type === "done") {
            setPersenLoading("100");
            return;
          }
          const percent = expectedTotal
            ? Math.min(90, 10 + Math.round((totalRows / expectedTotal) * 80))
            : event.type === "meta"
            ? 10
            : 20;
          setPersenLoading(percent.toString());
        },
      });
    } catch (error) {
      setLapAngsuranData([]);
      window.alert(error.message || "Gagal memuat laporan angsuran");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader persenLoading={persenLoading} />;
  }

  return (
    <Box>
      <Typography color="#757575">Laporan</Typography>
      <Typography variant="h4" sx={subTitleText}>
        Laporan Angsuran{historis ? " Historis" : ""}
      </Typography>
      <Divider sx={dividerStyle} />
      <Box sx={showDataWrapper}>
        <Row>
          <Col sm={6}>
            <Form.Group
              as={Row}
              className="mb-3"
              controlId="formPlaintextPassword"
            >
              <Form.Label column sm="3" style={textRight}>
                Per. Tanggal :
              </Form.Label>
              <Col sm="9">
                <DatePicker
                  dateFormat="dd/MM/yyyy"
                  selected={perTgl}
                  onChange={(e) => setPerTgl(e)}
                  customInput={
                    <TextField
                      error={error && perTgl === null && true}
                      helperText={
                        error && perTgl === null && "Per. Tanggal harus diisi!"
                      }
                      sx={{ width: "100%" }}
                      size="small"
                    />
                  }
                />
              </Col>
            </Form.Group>
          </Col>
        </Row>
      </Box>
      <Box sx={spacingTop}>
        <ButtonGroup variant="outlined" color="secondary">
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => downloadPdf()}
          >
            CETAK
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => {
              getLapAngsuran();
            }}
          >
            EXCEL
          </Button>
        </ButtonGroup>
      </Box>
      {lapAngsuranData.length > 0 && (
        <>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DownloadIcon />}
            sx={{ marginTop: "10px" }}
            onClick={() => downloadExcel(lapAngsuranData, perTgl, historis)}
          >
            EXCEL
          </Button>
          <Box sx={tableInfo}>
            Total data: {lapAngsuranData.length.toLocaleString("id-ID")}
          </Box>
        </>
      )}
      <Box sx={tableWrapper}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {lapAngsuranColumns.map((column) => (
                <th key={column} style={headerCellStyle}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lapAngsuranData.map((item, rowIndex) => (
              <tr key={`${item.kd_ar}-${item.ke}-${rowIndex}`}>
                {lapAngsuranColumns.map((column) => (
                  <td key={column} style={bodyCellStyle}>
                    {item[column]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export const LapAngsuranHistoris = () => <LapAngsuran historis />;

export default LapAngsuran;

const subTitleText = {
  fontWeight: "900",
};

const dividerStyle = {
  mt: 2,
};

const spacingTop = {
  mt: 4,
};

const showDataWrapper = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  // maxWidth: {
  //   md: "40vw"
  // }
};

const tableInfo = {
  mt: 3,
  mb: 1,
  color: "#475569",
  fontSize: "0.9rem",
  fontWeight: 600,
};

const tableWrapper = {
  mt: 1,
  maxHeight: "70vh",
  overflow: "auto",
  border: "1px solid #d7dee8",
  borderRadius: "6px",
  backgroundColor: "#fff",
};

const tableStyle = {
  width: "max-content",
  minWidth: "100%",
  borderCollapse: "collapse",
  fontSize: "0.82rem",
};

const headerCellStyle = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  padding: "10px 12px",
  backgroundColor: "#1976d2",
  color: "#fff",
  border: "1px solid #d7dee8",
  whiteSpace: "nowrap",
  textAlign: "left",
};

const bodyCellStyle = {
  padding: "8px 12px",
  border: "1px solid #e5eaf0",
  whiteSpace: "nowrap",
  textAlign: "left",
};
